/**
 * Capture-path timeout outcomes — WP-575 (TEN-2917, TEN-1126).
 *
 * A gateway budget abort on `chain.createEntry` is NOT a failed capture — it is an UNKNOWN
 * one, and that difference is the whole reason this module exists. The client stopped
 * waiting; the server may have written the entry. Before this, `tools/smart-capture.ts`
 * rethrew bare, so the agent was told the capture failed and would helpfully retry —
 * duplicating a write that had actually succeeded. TEN-1126 names the same thing on the
 * batch path: "ghost entries, no partial-result feedback or reconciliation path."
 *
 * Split out of smart-capture.ts rather than inlined (STD-2 / DEC-1504 shrink-only ratchet):
 * "what do we tell the caller when the outcome is unknowable" is a self-contained decision
 * with its own reason to change, and smart-capture.ts is already 3.4k lines of orchestration.
 */
import { failure, type ToolResult } from "../envelope.js";
import type { GatewayTimeoutError } from "../client.js";

/**
 * Did this throw come from a gateway latency-budget abort?
 *
 * Discriminates on `name`, NOT `instanceof`, and takes the class in as a TYPE only — so this
 * module never holds a runtime reference to `../client.js`'s class object. Two real reasons,
 * both of which bit before this shape was chosen:
 *   1. Class identity does not survive a mocked module. Every smart-capture test mocks
 *      `../client.js` with an explicit factory listing the exports it needs; an `instanceof`
 *      against a class such a factory doesn't re-export evaluates against `undefined` and
 *      throws "Right-hand side of 'instanceof' is not callable" — surfacing three layers away
 *      as an unrelated assertion failure. That is a trap for every future test that mocks the
 *      client, not a one-time fix.
 *   2. Class identity does not survive module duplication in general (a bundler emitting the
 *      module twice, an ESM/CJS split). `name` does.
 * This is the same technique `../client.js` itself uses one layer down to recognize
 * `AbortSignal.timeout`'s DOMException, which is likewise not an importable class. The `name`
 * value depended on here is pinned by `../__tests__/route-latency-budget.test.ts`.
 */
export function isGatewayTimeout(error: unknown): error is GatewayTimeoutError {
  return (error as { name?: string } | null | undefined)?.name === "GatewayTimeoutError";
}

/**
 * The single-capture answer: report what is actually known instead of a bare failure.
 *
 * WP-575 review round (P1): this used to assert `didNotRun: ["relations", "commit",
 * "coaching"]` — wrong for two of the three. `commit` and `coaching` both run INSIDE
 * `chain.createEntry` before it returns (convex/agentKnowledge/entries.ts: coaching at
 * ~683-692/715, Open-mode auto-commit exposed via `insertResult.status` at ~706), so if the
 * server finished after this client gave up waiting, they already ran — same as the entry
 * itself. Only relation linking (`chain.createEntryRelations`) is a genuinely SEPARATE
 * mutation issued later by this file and never reached when create throws. Asserting
 * "definitely did not run" for commit/coaching told the agent it was safe to assume no
 * server-side side effect existed, which is exactly backwards for the Open-mode default.
 */
export function buildCaptureOutcomeUnknownResult(
  error: GatewayTimeoutError,
  name: string,
  msg: string,
): ToolResult {
  if (!error.mayHaveLanded) return buildDryRunTimeoutResult(error, msg);
  const recovery =
    `Search for "${name}" before retrying — a retry will duplicate it if the write landed. ` +
    `If found, read back its commit status and coaching output before assuming either is missing.`;
  return {
    content: [{
      type: "text" as const,
      text:
        `# Capture Outcome Unknown — Not Failed\n\n${msg}\n\n` +
        `**What is known:** the request reached the server and this client gave up waiting ` +
        `after ${error.elapsedMs}ms. The entry **may or may not** have been written.\n\n` +
        `**What definitely did NOT run:** relation linking — a separate mutation issued ` +
        `after the create returns, so it is never reached when create itself times out.\n\n` +
        `**What may also have run — outcome unknown, same reason as the entry itself:** ` +
        `auto-commit and quality coaching. Both execute INSIDE the create call, before it ` +
        `returns, so if the server finished after this client stopped waiting, they ran too. ` +
        `Do not assume either is absent — read the entry back to find out.\n\n` +
        `**Do this next:** ${recovery}`,
    }],
    structuredContent: failure(
      "CAPTURE_OUTCOME_UNKNOWN",
      msg,
      recovery,
      [{ tool: "entries", description: "Search for the entry before retrying", parameters: { action: "search", query: name } }],
      {
        // Structured so a caller can branch on this without parsing prose — the
        // reconciliation signal TEN-1126 says the capture path never had.
        partialSuccess: true,
        mayHaveLanded: error.mayHaveLanded,
        budgetMs: error.budgetMs,
        elapsedMs: error.elapsedMs,
        completedBeforeTimeout: [] as string[],
        // WP-575 review (P1): genuinely definite — relation linking is a separate mutation
        // never reached when create throws. See the doc comment above for why `commit` and
        // `coaching` were removed from this list.
        didNotRun: ["relations"],
        // WP-575 review (P1): distinct from `didNotRun` on purpose — these ran INSIDE the
        // same server call as the create, so their outcome is exactly as unknown as the
        // entry's. A caller must read the entry back, never assume either ran or didn't.
        mayHaveRun: ["commit", "coaching"],
      },
    ),
  };
}

/**
 * The DRY-RUN answer (PR #533 review, Codex P2).
 *
 * A `preview: true` capture that times out is the one case where "did the write land" has a
 * definite answer: no. The server's preview branch returns before any insert
 * (convex/agentKnowledge/entries.ts:498-599, first write at :601), so nothing — entry,
 * auto-commit, coaching, or relation linking — can have happened. The generic path above
 * would tell the agent its write "may or may not" have been written and to search before
 * retrying, which is not merely unhelpful on a dry run: the batch-preview surface renders that
 * warning directly beside its own "no DB writes" line, so one response asserted both.
 *
 * The code stays `CAPTURE_OUTCOME_UNKNOWN` because something genuinely IS unknown — the
 * validation VERDICT the preview existed to produce. What is now known is that nothing landed,
 * which is what makes it plainly safe to re-run.
 */
function buildDryRunTimeoutResult(error: GatewayTimeoutError, msg: string): ToolResult {
  const recovery = "Re-run the preview — nothing was written, so there is nothing to search for and nothing to duplicate.";
  return {
    content: [{
      type: "text" as const,
      text:
        `# Preview Outcome Unknown — Nothing Was Written\n\n${msg}\n\n` +
        `**What is known:** the request reached the server and this client gave up waiting ` +
        `after ${error.elapsedMs}ms. This was a **preview**, and the server returns its ` +
        `validation result before performing any write — so no entry, auto-commit, quality ` +
        `coaching, or relation linking happened.\n\n` +
        `**What is unknown:** the validation verdict this preview was asked for.\n\n` +
        `**Do this next:** ${recovery}`,
    }],
    structuredContent: failure(
      "CAPTURE_OUTCOME_UNKNOWN",
      msg,
      recovery,
      [],
      {
        // No partial success to reconcile: a dry run has no server-side state to converge on.
        partialSuccess: false,
        mayHaveLanded: false,
        budgetMs: error.budgetMs,
        elapsedMs: error.elapsedMs,
        completedBeforeTimeout: [] as string[],
        didNotRun: ["entry", "relations", "commit", "coaching"],
        mayHaveRun: [] as string[],
      },
    ),
  };
}

/**
 * The batch answer (TEN-1126). The entry is still reported failed so the batch summary stays
 * honest about what it can PROVE, but the message now says the write may have landed and
 * names the one action that resolves it — instead of a bare transport error the caller could
 * only read as "didn't happen", which is exactly how a ghost entry gets made.
 */
export function describeBatchEntryError(error: unknown, entryName: string): string {
  const msg = error instanceof Error ? error.message : String(error);
  if (!isGatewayTimeout(error)) return msg;
  // A dry run cannot have been captured, so "it may be captured twice" would be false — and
  // the preview surface prints this line beside its own "no DB writes" note (PR #533 review).
  return error.mayHaveLanded
    ? `${msg} Outcome unknown for this entry — search for "${entryName}" before re-running the batch, or it may be captured twice.`
    : `${msg} This was a preview, so nothing was written for this entry — safe to re-run the batch.`;
}
